/**
 * Public respondent-journey service.
 *
 * Talks to the unauthenticated `/public/respondent/<token>/…` endpoints. No JWT
 * is ever attached — a per-session `session_token` (returned by `begin`)
 * authorises the follow-up calls.
 */

import { BaseService } from './baseService'
import type { CalibrationVariant } from '@/lib/calibrationVariants'

export interface RespondentChoice {
  value: number
  text: string
  skip_questions?: number | null
}

export interface RespondentQuestion {
  id: number
  unique_number: number
  text: string
  question_type: 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'
  section_name: string
  choices: RespondentChoice[]
}

export interface RespondentStimulus {
  id: number
  /** `figma` = a clickable prototype task (Pro, Z-98): `payload` is the task,
   *  `exposure_ms` the time limit, and the embed address arrives only with the
   *  session (`begin().prototype_embeds`), never in this public payload. */
  type: 'video' | 'image' | 'url' | 'text' | 'figma'
  title: string
  url: string
  storage_url: string
  payload: string
  order: number
  exposure_ms: number | null
  /** Figma only: which screen the prototype opens on and which one ends the
   *  task (empty = no goal, the respondent finishes on their own). */
  config?: { start_node_id?: string; goal_node_id?: string }
}

/** What a respondent did inside one prototype, on the recording clock (ms). */
export interface PrototypeSession {
  stimulus_id: number
  started_ms: number
  ended_ms: number
  outcome: 'success' | 'gave_up' | 'timeout' | 'finished'
  /** Where the prototype sat in the viewport, as fractions. */
  frame: { x: number; y: number; w: number; h: number } | null
  events: Array<
    | { t_ms: number; type: 'screen'; node_id: string }
    | { t_ms: number; type: 'click'; node_id: string; x: number; y: number; handled: boolean }
  >
}

export interface RespondentStudy {
  share_token: string
  name: string
  client: string
  description: string
  require_access_code: boolean
  /** When true, per-frame gaze is computed server-side during analysis (no
   *  extra respondent screen) and shown as a gaze dot in the research panel. */
  eye_tracking_enabled: boolean
  /** Which calibration protocol to run (Z-45) — inert when eye tracking is
   *  off. The respondent app resolves this id via its own fixed table
   *  (`@/lib/calibrationVariants`), not a server lookup. */
  calibration_variant: CalibrationVariant
  /** A prototype task is desktop-only: phones are turned away before consent. */
  requires_desktop?: boolean
  instructions: string
  question_timing: 'start' | 'end'
  stimuli: RespondentStimulus[]
  questions: RespondentQuestion[]
}

export interface RespondentAnswer {
  question_id: number
  choices?: number[]
  text_answer?: string
}

/** One stimulus window relative to the recording start, logged during playback. */
export interface RespondentMarker {
  stimulus_id: number
  order: number
  start_ms: number
  end_ms: number
}

/** The 7 canonical emotion keys (matches the analytics EMOTION_ORDER). */
export type RespondentEmotionKey =
  | 'happy'
  | 'surprised'
  | 'neutral'
  | 'sad'
  | 'angry'
  | 'disgusted'
  | 'fearful'

export interface RespondentStimulusResult {
  stimulus_id: number
  order: number
  title: string
  type: 'video' | 'image' | 'url' | 'text' | 'figma'
  exposure_ms: number | null
  duration_ms: number
  /** Offset (ms) of this stimulus within the whole-study face recording. */
  window_start_ms: number
  grid_ms: number[]
  /** Per-emotion values on `grid_ms`; `null` marks a grid point with no
   *  analysed face (the timeline breaks its line there). */
  series: Record<RespondentEmotionKey, (number | null)[]>
  has_data: boolean
}

export type RespondentResultsStatus =
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'no_data'
  | 'no_recording'

export interface RespondentResults {
  status: RespondentResultsStatus
  emotions: RespondentEmotionKey[]
  results: RespondentStimulusResult[]
}

class RespondentService extends BaseService {
  /** Load everything needed to render the journey for a share token.
   *
   *  Pass `sessionToken` when the caller already has a session — reopening
   *  one's own results, say. A study that has stopped taking new respondents
   *  (its package spent, its limit reached, its date passed) answers a stranger
   *  with a 404, and without this the person who filled the very last slot
   *  would be locked out of the results they just produced.
   */
  async getStudy(token: string, sessionToken?: string): Promise<RespondentStudy> {
    const query = sessionToken ? `?session_token=${encodeURIComponent(sessionToken)}` : ''
    return this.requestWithoutAuth<RespondentStudy>(
      `/public/respondent/${encodeURIComponent(token)}/${query}`,
      { method: 'GET' },
    )
  }

  /** Record consent and open a session. Returns the session token.
   *
   * When an `invite` token is supplied (from an emailed link), the session binds
   * to that pre-created, tracked participant; otherwise an anonymous one is made.
   */
  async begin(
    token: string,
    consent: {
      consent_data: boolean
      consent_age: boolean
      /** Acknowledgement of the RODO information clause (GDPR art. 13). */
      consent_rodo: boolean
      access_code?: string
      invite?: string
    },
  ): Promise<{
    session_token: string
    participant_id: number
    /** Figma embed address per prototype stimulus id, given only after consent. */
    prototype_embeds?: Record<string, string>
  }> {
    return this.requestWithoutAuth(`/public/respondent/${encodeURIComponent(token)}/begin/`, {
      method: 'POST',
      body: JSON.stringify(consent),
    })
  }

  /** Persist questionnaire answers for the session. */
  async submitAnswers(
    token: string,
    sessionToken: string,
    answers: RespondentAnswer[],
  ): Promise<{ saved: number }> {
    return this.requestWithoutAuth(`/public/respondent/${encodeURIComponent(token)}/answers/`, {
      method: 'POST',
      body: JSON.stringify({ session_token: sessionToken, answers }),
    })
  }

  /** Upload the webcam capture (multipart) and trigger emotion analysis.
   *
   * Uses XMLHttpRequest rather than fetch so the caller can render a live
   * progress bar — fetch has no upload-progress events.
   */
  uploadRecording(
    token: string,
    sessionToken: string,
    video: Blob,
    meta?: {
      durationSeconds?: number
      fps?: number
      /** Per-stimulus windows within the recording, for per-stimulus emotion slicing. */
      markers?: Array<{ stimulus_id?: number; order?: number; start_ms: number; end_ms: number }>
      /** Gaze-calibration schedule captured inside the recording (eye-tracking
       *  studies). Two consumers, two coordinate systems: the server-side
       *  affine fit uses content-normalised `x`/`y`, the gaze container uses
       *  `viewport_x`/`viewport_y` (it answers in viewport pixels). `stage`
       *  separates the dots fitted on from the dots scored against — without
       *  the latter the container refuses the session outright. */
      eyeTrackingCalibration?: {
        points: Array<{
          x: number
          y: number
          viewport_x?: number
          viewport_y?: number
          start_ms: number
          end_ms: number
          pose?: string
          stage?: 'calibration' | 'validation'
        }>
      } | null
      /** Real screen/viewport geometry measured in this browser at recording
       *  time (Z-45/Z-42) — never a hard-coded guess. */
      screenGeometry?: {
        screen_w_px: number
        screen_h_px: number
        viewport_w_px: number
        viewport_h_px: number
        device_pixel_ratio: number
      } | null
      /** Small face still (JPEG) grabbed at record start; becomes the
       *  respondent's avatar and outlives the RODO-deleted video. Best-effort. */
      thumbnail?: Blob | null
      /** Clicks and screen changes inside each Figma prototype task. */
      prototypeSessions?: PrototypeSession[]
    },
    /** Called with bytes sent / bytes total as the upload progresses. */
    onProgress?: (sentBytes: number, totalBytes: number) => void,
  ): Promise<{ recording_id: number; analysis_job_id: number | null }> {
    const filename = video.type.toLowerCase().includes('mp4') ? 'recording.mp4' : 'recording.webm'
    const form = new FormData()
    form.append('session_token', sessionToken)
    form.append('video_file', video, filename)
    if (meta?.durationSeconds != null) form.append('duration_seconds', String(meta.durationSeconds))
    if (meta?.fps != null) form.append('fps', String(meta.fps))
    if (meta?.markers && meta.markers.length) form.append('markers', JSON.stringify(meta.markers))
    if (meta?.eyeTrackingCalibration && meta.eyeTrackingCalibration.points.length) {
      form.append('eye_tracking_calibration', JSON.stringify(meta.eyeTrackingCalibration))
    }
    if (meta?.screenGeometry) form.append('screen_geometry', JSON.stringify(meta.screenGeometry))
    if (meta?.thumbnail) form.append('thumbnail_file', meta.thumbnail, 'thumbnail.jpg')
    if (meta?.prototypeSessions && meta.prototypeSessions.length) {
      form.append('prototype_sessions', JSON.stringify(meta.prototypeSessions))
    }

    const url = this.urlBuilder.build(`/public/respondent/${encodeURIComponent(token)}/recording/`)
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.responseType = 'json'
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded, e.total)
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response as { recording_id: number; analysis_job_id: number | null })
        } else {
          const err = (xhr.response ?? {}) as { error?: string; detail?: string }
          reject(new Error(err.error || err.detail || `Upload failed (${xhr.status})`))
        }
      }
      xhr.onerror = () => reject(new Error('Upload failed (network error)'))
      xhr.onabort = () => reject(new Error('Upload aborted'))
      xhr.send(form)
    })
  }

  /** Mark the journey complete. */
  async complete(token: string, sessionToken: string): Promise<{ status: string }> {
    return this.requestWithoutAuth(`/public/respondent/${encodeURIComponent(token)}/complete/`, {
      method: 'POST',
      body: JSON.stringify({ session_token: sessionToken }),
    })
  }

  /**
   * Absolute URL streaming the respondent's own face capture (range requests
   * supported), authorised by the session token. Usable directly as a `<video>`
   * `src` on the "See your results" screen.
   */
  recordingUrl(token: string, sessionToken: string): string {
    const base = this.urlBuilder.build(
      `/public/respondent/${encodeURIComponent(token)}/recording/`,
    )
    return `${base}?session_token=${encodeURIComponent(sessionToken)}`
  }

  /**
   * Fetch the respondent's own emotion results. Poll this while `status` is
   * `processing` — the analysis runs asynchronously and may take a while.
   */
  async getResults(token: string, sessionToken: string): Promise<RespondentResults> {
    return this.requestWithoutAuth<RespondentResults>(
      `/public/respondent/${encodeURIComponent(token)}/results/`,
      {
        method: 'POST',
        body: JSON.stringify({ session_token: sessionToken }),
      },
    )
  }
}

export const respondentService = new RespondentService()
